import { Base } from '@studiometa/js-toolkit';
import fetchSection from '../../utils/fetchSection.js';

/**
 * Search overlay with predictive results.
 */
export default class Search extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'Search',
    refs: ['form', 'input', 'close[]', 'overlay', 'container', 'loader', 'status'],
    emits: ['open', 'close'],
    options: {
      url: String,
      searchUrl: String,
      limit: {
        type: Number,
        default: 6,
      },
      delay: {
        type: Number,
        default: 400,
      },
    },
  };

  isOpen = false;

  timeout = null;

  /**
   * @type {AbortController|null}
   */
  controller = null;

  /**
   * The element focused before opening, focused back on close.
   * @type {HTMLElement|null}
   */
  trigger = null;

  /**
   * Keep the default content to restore it when the input is cleared.
   * @returns {void}
   */
  mounted() {
    this.defaultContent = this.$refs.container.innerHTML;
  }

  /**
   * @returns {string}
   */
  get searchTerm() {
    return this.$refs.input.value.trim();
  }

  /**
   * Debounce the search while typing.
   * @returns {void}
   */
  onInputInput() {
    clearTimeout(this.timeout);
    this.controller?.abort();

    if (!this.searchTerm) {
      this.reset();
      return;
    }

    this.$refs.loader.hidden = false;
    this.timeout = setTimeout(() => this.search(this.searchTerm), this.$options.delay);
  }

  /**
   * Render the predictive results as soon as they arrive, then complete them with the
   * total count, which is slower and optional.
   * @param {string} term The search term.
   * @returns {Promise<void>}
   */
  async search(term) {
    this.controller = new AbortController();
    const { signal } = this.controller;
    const { url, searchUrl, limit } = this.$options;
    const query = encodeURIComponent(term);

    const countRequest = fetchSection(
      `${searchUrl}?q=${query}&options[prefix]=last`,
      'mx-search-count',
      signal,
    ).catch(() => null);

    try {
      const results = await fetchSection(
        `${url}?q=${query}&resources[type]=product,query&resources[limit]=${limit}&resources[limit_scope]=each&resources[options][fields]=title,body,variants.title`,
        'mx-predictive-search',
        signal,
      );

      this.render(results.innerHTML);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.reset();
        globalThis.reportError?.(error);
      }

      return;
    }

    const count = (await countRequest)?.querySelector('[data-count]');
    const countElement = this.$refs.container.querySelector('[data-search-count]');

    if (signal.aborted || !count || !countElement) {
      return;
    }

    countElement.textContent = `(${count.dataset.count})`;

    if (Number(count.dataset.count) === 0) {
      return;
    }

    // Pages or articles may match without any product.
    const more = this.$refs.container.querySelector('[data-search-more]');

    if (more) {
      more.hidden = false;
      this.$refs.container.querySelector('[data-search-empty]')?.remove();
    }

    this.$refs.status.textContent = count.dataset.status;
  }

  /**
   * @param {string} html The content to display.
   * @returns {void}
   */
  render(html) {
    this.$refs.loader.hidden = true;
    this.$refs.container.innerHTML = html;
    this.$refs.status.textContent =
      this.$refs.container.querySelector('[data-search-status]')?.textContent.trim() ?? '';
  }

  /**
   * Restore the default content.
   * @returns {void}
   */
  reset() {
    clearTimeout(this.timeout);
    this.controller?.abort();
    this.render(this.defaultContent);
  }

  /**
   * Sync the expanded state of the buttons controlling the search.
   * @param {boolean} isExpanded
   * @returns {void}
   */
  setExpanded(isExpanded) {
    for (const button of document.querySelectorAll(`[aria-controls="${this.$el.id}"]`)) {
      button.setAttribute('aria-expanded', String(isExpanded));
    }
  }

  /**
   * Open the search.
   * @returns {void}
   */
  open() {
    if (this.isOpen) {
      return;
    }

    this.isOpen = true;
    this.trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.documentElement.classList.add('is-search-open');
    this.$el.removeAttribute('inert');
    this.$el.setAttribute('aria-hidden', 'false');
    this.setExpanded(true);
    this.$refs.input.focus();
    this.$emit('open');
  }

  /**
   * Close the search.
   * @param {object} [options] Options
   * @param {boolean} [options.restoreFocus] Focus back the element focused before opening.
   * @returns {void}
   */
  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    document.documentElement.classList.remove('is-search-open');
    this.$el.setAttribute('inert', '');
    this.$el.setAttribute('aria-hidden', 'true');
    this.setExpanded(false);
    this.$refs.input.value = '';
    this.reset();

    if (restoreFocus) {
      this.trigger?.focus();
    }

    this.trigger = null;
    this.$emit('close');
  }

  /**
   * @returns {void}
   */
  onCloseClick() {
    this.close();
  }

  /**
   * @returns {void}
   */
  onOverlayClick() {
    this.close();
  }

  /**
   * Close when the focus moves outside, the panel is not modal: the header stays usable.
   * @param {object} props Props
   * @param {FocusEvent} props.event The focus event.
   * @returns {void}
   */
  onDocumentFocusin({ event }) {
    if (this.isOpen && !this.$el.contains(event.target)) {
      this.close({ restoreFocus: false });
    }
  }

  /**
   * Close on Escape.
   * @param {object} props Props
   * @param {boolean} props.isDown Whether the key is pressed.
   * @param {boolean} props.ESC Whether the key is Escape.
   * @returns {void}
   */
  keyed({ isDown, ESC }) {
    if (this.isOpen && isDown && ESC) {
      this.close();
    }
  }
}
